import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export const AccountSettingsDialog = ({ open, onOpenChange }) => {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", current_password: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && user) { setForm({ name: user.name || "", email: user.email || "", current_password: "" }); }
  }, [open, user]);

  const submit = async () => {
    if (!form.current_password) { toast.error("Please enter your current password"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-account", form);
      setUser(data);
      toast.success("Account details updated. Use your new email next time you sign in.");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update account");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-md" data-testid="account-settings-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Account Settings</DialogTitle>
          <DialogDescription>Change your display name or the email you sign in with. Your current password is required.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Display name</Label>
            <Input data-testid="account-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Sign-in email (username)</Label>
            <Input type="email" data-testid="account-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label>Current password</Label>
            <Input type="password" data-testid="account-current-password-input" value={form.current_password}
              onChange={(e) => setForm({ ...form, current_password: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="account-save-button" className="bg-[#2D5A27] hover:bg-[#23471F] text-white">
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
